import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Briefcase, Calendar, Wallet, TrendingUp } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface PortageClient {
  id: string;
  client_name: string;
  mission_label: string;
  montant_contrat: number;
  ca_realise: number;
  ca_previsionnel: number;
  date_debut: string;
  date_fin: string;
  statut: string;
  type: string;
}

interface Summary {
  total_contrat: number;
  total_realise: number;
  total_previsionnel: number;
  nb_clients: number;
  nb_missions: number;
}

const fmtEur = (n: number) =>
  n.toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const fmtDate = (d: string) => {
  if (!d) return "—";
  const [y, m] = d.split("-");
  const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
};

export default function PortageClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<PortageClient[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/portage/clients`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setClients(data.clients || []);
          setSummary(data.summary || null);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const grouped = clients.reduce<Record<string, PortageClient[]>>((acc, c) => {
    (acc[c.client_name] ||= []).push(c);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border/50 bg-background/95 px-4 py-3 backdrop-blur">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15">
          <Briefcase className="h-4 w-4 text-primary" />
        </div>
        <div>
          <h1 className="text-lg font-bold leading-tight">Clients — Portage Salarial</h1>
          <p className="text-xs text-muted-foreground">Activité consultant de Joël sous statut ITG</p>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {summary && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card>
              <CardContent className="p-3">
                <p className="text-[10px] uppercase text-muted-foreground">Clients</p>
                <p className="text-xl font-bold">{summary.nb_clients}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-[10px] uppercase text-muted-foreground">Missions</p>
                <p className="text-xl font-bold">{summary.nb_missions}</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-500/30 bg-emerald-500/5">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase text-muted-foreground">CA réalisé</p>
                <p className="text-xl font-bold text-emerald-500">{fmtEur(summary.total_realise)}</p>
              </CardContent>
            </Card>
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase text-muted-foreground">Prévisionnel restant</p>
                <p className="text-xl font-bold text-amber-500">{fmtEur(summary.total_previsionnel)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {Object.entries(grouped).map(([clientName, missions]) => {
          const clientTotal = missions.reduce((s, m) => s + m.montant_contrat, 0);
          const clientRealise = missions.reduce((s, m) => s + m.ca_realise, 0);
          return (
            <Card key={clientName}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{clientName}</CardTitle>
                  <Badge variant="outline">{fmtEur(clientTotal)} contrat</Badge>
                </div>
                <CardDescription>
                  {missions.length} mission{missions.length > 1 ? "s" : ""} · {fmtEur(clientRealise)} réalisé
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {missions.map((m) => (
                  <div
                    key={m.id}
                    className="flex flex-col gap-2 rounded-lg border border-border/50 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{m.mission_label}</p>
                      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {fmtDate(m.date_debut)} → {fmtDate(m.date_fin)}
                        <Badge
                          variant="outline"
                          className={`ml-1 h-4 px-1.5 text-[9px] ${
                            m.statut === "en_cours"
                              ? "border-emerald-500/40 text-emerald-500"
                              : "border-muted-foreground/30 text-muted-foreground"
                          }`}
                        >
                          {m.statut === "en_cours" ? "En cours" : "Terminé"}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-right">
                      <div>
                        <p className="flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
                          <Wallet className="h-3 w-3" /> Réalisé
                        </p>
                        <p className="text-sm font-bold text-emerald-500">{fmtEur(m.ca_realise)}</p>
                      </div>
                      {m.ca_previsionnel > 0 && (
                        <div>
                          <p className="flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
                            <TrendingUp className="h-3 w-3" /> Prévisionnel
                          </p>
                          <p className="text-sm font-bold text-amber-500">{fmtEur(m.ca_previsionnel)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}

        <p className="px-1 text-center text-[11px] text-muted-foreground">
          Statut : consultant indépendant sous portage salarial ITG — distinct du produit Grafton IA.
        </p>
      </div>
    </div>
  );
}
